import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
  Tab,
  Tabs,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableViewIcon from "@mui/icons-material/TableView";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

/* eslint-disable react/prop-types */

const exportColumns = [
  ["fee_head_id", "Fee Head ID"],
  ["fee_head_name", "Fee Head Name"],
  ["fee_type_name", "Fee Type"],
  ["fee_amount", "Amount"],
  ["enroll_type", "Enroll Type"],
  ["pay_type", "Pay Type"],
  ["status", "Status"],
  ["start_date", "Start Date"],
  ["end_date", "End Date"],
];

const formatDate = (value) => (value ? new Date(value).toLocaleDateString("en-IN") : "");

export default function FeeHeadApproval() {
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const modifiedBy = user?.user_email || user?.username || "";
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [activeTab, setActiveTab] = useState(0);
  const [allRows, setAllRows] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [exportMenu, setExportMenu] = useState({ anchorEl: null, rows: [], label: "" });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [pageSize, setPageSize] = useState(10);
  const showMessage = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const fetchData = useCallback(async () => {
    if (!clientId || !collegeId || !academicYear) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/fee-head-approvals/${clientId}/${collegeId}?academic_year=${encodeURIComponent(
          academicYear
        )}`
      );
      const items = Array.isArray(data?.data) ? data.data : data?.data?.items || [];
      setAllRows(
        items.map((row) => ({
          ...row,
          id: String(row.fee_head_id),
          fee_type_name: row.feeType?.fee_type_name || "",
        }))
      );
    } catch (error) {
      showMessage(error?.response?.data?.message || "Unable to load Fee Head approvals.", "error");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, clientId, collegeId, academicYear]);

  useEffect(() => {
    const fetchAcademicYears = async () => {
      if (!clientId || !collegeId) return;
      try {
        const { data } = await axios.get(
          `${API_BASE_URL}/api/fee-head-approvals/academic-years/${clientId}/${collegeId}`
        );
        const years = data?.data || [];
        setAcademicYears(years);
        setAcademicYear((current) => current || years[0] || "");
      } catch (error) {
        showMessage(error?.response?.data?.message || "Unable to load academic years.", "error");
      }
    };
    fetchAcademicYears();
  }, [API_BASE_URL, clientId, collegeId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredRows = useMemo(() => {
    const term = appliedSearch.trim().toLowerCase();
    if (!term) return allRows;
    return allRows.filter((row) =>
      [
        row.fee_head_id,
        row.fee_head_name,
        row.fee_type_name,
        row.academic_year,
        row.fee_amount,
        row.enroll_type,
        row.pay_type,
        row.status,
        row.workflow_status,
        row.created_by,
      ].some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(term)
      )
    );
  }, [allRows, appliedSearch]);

  const draft_Rows = filteredRows.filter((row) => row.workflow_status === "DRAFT");
  const approved_Rows = filteredRows.filter((row) => row.workflow_status === "APPROVED");
  const draftRows = draft_Rows.map((row, index) => ({
    ...row,
    sno: index + 1,
  }));
  const approvedRows = approved_Rows.map((row, index) => ({
    ...row,
    sno: index + 1,
  }));
  const runAction = async () => {
    if (!confirm) return;
    const { action, row } = confirm;
    setConfirm(null);
    try {
      let response;
      if (["approve", "reject", "decline"].includes(action)) {
        response = await axios.patch(
          `${API_BASE_URL}/api/fee-head-approvals/${clientId}/${collegeId}/${row.fee_head_id}/${action}`,
          { modified_by: modifiedBy }
        );
      } else {
        response = await axios.delete(
          `${API_BASE_URL}/api/fee-head-approvals/${clientId}/${collegeId}/${row.fee_head_id}`,
          { data: { modified_by: modifiedBy } }
        );
      }
      showMessage(
        response?.data?.message ||
          (action === "delete"
            ? "Fee Head approval deleted successfully."
            : action === "approve"
            ? "Fee Head approved successfully."
            : action === "decline"
            ? "Fee Head approval declined successfully."
            : "Fee Head approval rejected successfully.")
      );
      await fetchData();
    } catch (error) {
      showMessage(error?.response?.data?.message || "Unable to update this approval.", "error");
    }
  };

  const getExportValue = (row, field) => {
    const value = row[field];
    return field.endsWith("_date") || field.endsWith("_on") ? formatDate(value) : value ?? "";
  };

  const exportExcel = (rows, label) => {
    const data = rows.map((row) =>
      Object.fromEntries(exportColumns.map(([field, title]) => [title, getExportValue(row, field)]))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(data), label);
    XLSX.writeFile(workbook, `Fee_Head_${label}.xlsx`);
  };

  const exportPdf = (rows, label) => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.text(`Fee Head ${label}`, 14, 12);
    autoTable(doc, {
      startY: 18,
      head: [exportColumns.map(([, title]) => title)],
      body: rows.map((row) => exportColumns.map(([field]) => getExportValue(row, field))),
      styles: { fontSize: 6 },
      headStyles: { fillColor: [25, 118, 210] },
    });
    doc.save(`Fee_Head_${label}.pdf`);
  };

  const baseColumns = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No",
        width: 80,
      },
      { field: "fee_head_id", headerName: "ID", width: 80 },
      { field: "fee_head_name", headerName: "Fee Head Name", minWidth: 380, flex: 1 },
      { field: "fee_type_name", headerName: "Fee Type", minWidth: 150, flex: 0.7 },
      { field: "fee_amount", headerName: "Amount", width: 110 },
      { field: "enroll_type", headerName: "Enroll Type", width: 115 },
      { field: "pay_type", headerName: "Pay Type", width: 105 },
      { field: "status", headerName: "Status", width: 100 },
      {
        field: "start_date",
        headerName: "Start Date",
        width: 110,
        valueFormatter: ({ value }) => formatDate(value),
      },
      {
        field: "end_date",
        headerName: "End Date",
        width: 110,
        valueFormatter: ({ value }) => formatDate(value),
      },
    ],
    []
  );

  const actionColumn = (approved) => ({
    field: "actions",
    headerName: "Actions",
    width: approved ? 130 : 230,
    sortable: false,
    renderCell: ({ row }) =>
      approved ? (
        <Button
          size="medium"
          variant="contained"
          startIcon={<CancelIcon />}
          sx={{
            backgroundColor: "#d32f2f",
            color: "#fff",
            "&:hover": { backgroundColor: "#b71c1c" },
          }}
          onClick={() => setConfirm({ action: "decline", row })}
        >
          Decline
        </Button>
      ) : (
        <Stack direction="row" spacing={1}>
          <Button
            size="medium"
            variant="contained"
            startIcon={<CheckCircleIcon />}
            sx={{
              backgroundColor: "#2e7d32",
              color: "#fff",
              "&:hover": { backgroundColor: "#1b5e20" },
            }}
            onClick={() => setConfirm({ action: "approve", row })}
          >
            Accept
          </Button>
          <Button
            size="medium"
            variant="contained"
            startIcon={<CancelIcon />}
            sx={{
              backgroundColor: "#d32f2f",
              color: "#fff",
              "&:hover": { backgroundColor: "#b71c1c" },
            }}
            onClick={() => setConfirm({ action: "reject", row })}
          >
            Reject
          </Button>
        </Stack>
      ),
  });

  const renderGridSection = (title, rows, approved) => (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ sm: "center" }}
        mb={1}
      >
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          {title} ({rows.length})
        </Typography>

        <Button
          variant="contained"
          startIcon={<MoreVertIcon />}
          disabled={!rows.length}
          sx={{
            backgroundColor: "#1976d2",
            color: "#fff",
            "&:hover": { backgroundColor: "#115293" },
          }}
          onClick={(event) =>
            setExportMenu({
              anchorEl: event.currentTarget,
              rows,
              label: approved ? "Approved" : "Draft",
            })
          }
        >
          Actions
        </Button>
      </Stack>
      <Box sx={{ height: 430, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={[...baseColumns, actionColumn(approved)]}
          loading={loading}
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          pagination
          disableSelectionOnClick
        />
      </Box>
    </Paper>
  );

  const confirmationText =
    confirm?.action === "delete"
      ? "Are you sure you want to delete this approval?"
      : confirm?.action === "decline"
      ? "Are you sure you want to decline this approval?"
      : confirm?.action === "reject"
      ? "Are you sure you want to reject this approval?"
      : "Are you sure you want to approve this Fee Head?";

  return (
    <Box p={2}>
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Typography variant="h5" sx={{ flexGrow: 1 }}>
              APPROVE FEE HEADS
            </Typography>
            <TextField
              select
              size="medium"
              label="Academic Year"
              value={academicYear}
              onChange={(event) => setAcademicYear(event.target.value)}
              SelectProps={{ native: true }}
              sx={{ width: { xs: "100%", sm: 180 } }}
            >
              {academicYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </TextField>
            <TextField
              size="medium"
              label="Search Fee Heads"
              placeholder="Name, fee type, amount or status"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && setAppliedSearch(search)}
              sx={{
                width: { xs: "100%", sm: 300 },
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#fff !important",
                  borderRadius: 1,
                },
                "& .MuiInputBase-input": {
                  color: "#000 !important",
                  WebkitTextFillColor: "#000 !important",
                },
                "& input:-webkit-autofill": {
                  WebkitBoxShadow: "0 0 0 100px #fff inset !important",
                  WebkitTextFillColor: "#000 !important",
                  caretColor: "#000",
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#555",
                  opacity: 1,
                },
                "& .MuiInputLabel-root": { color: "#555" },
                "& .MuiInputLabel-root.Mui-focused": { color: "#1976d2" },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#bdbdbd" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#1976d2" },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#1976d2",
                },
              }}
            />
            <Button
              size="medium"
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={() => setAppliedSearch(search)}
              sx={{
                minWidth: 100,
                backgroundColor: "#1976d2 !important",
                color: "#fff !important",
                "& .MuiButton-startIcon": { color: "#fff" },
                "&:hover": { backgroundColor: "#115293 !important" },
              }}
            >
              Search
            </Button>
          </Stack>
        </Paper>

        {/* Fee Head Approval Tabs */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(event, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: 66,
              mb: 2,
              borderBottom: "1px solid #e0e0e0",

              "& .MuiTabs-indicator": {
                height: 4,
                borderRadius: "4px 4px 0 0",
                backgroundColor: "#455a64",
              },

              "& .MuiTab-root": {
                minHeight: 60,
                minWidth: { xs: 155, sm: 220 },
                px: 2.5,
                py: 1.5,
                mr: 1,
                borderRadius: "8px 8px 0 0",
                fontWeight: 700,
                fontSize: { xs: 13, sm: 15 },
                textTransform: "none",
                opacity: 1,
                transition: "background-color 0.2s ease",
              },

              /* Pending - Light Orange */
              "& .MuiTab-root:nth-of-type(1)": {
                backgroundColor: "#FFF3E0",
                color: "#9A5300",
                "&:hover": {
                  backgroundColor: "#FFE0B2",
                },
              },

              /* Approved - Light Green */
              "& .MuiTab-root:nth-of-type(2)": {
                backgroundColor: "#E8F5E9",
                color: "#1B5E20",
                "&:hover": {
                  backgroundColor: "#C8E6C9",
                },
              },

              "& .MuiTab-root.Mui-selected": {
                fontWeight: 800,
                boxShadow: "inset 0 -4px 0 #455a64",
              },

              "& .MuiTabs-scrollButtons": {
                color: "#1976d2",
              },
            }}
          >
            <Tab label={`Pending Approvals (${draftRows.length})`} />
            <Tab label={`Approved Fee Heads (${approvedRows.length})`} />
          </Tabs>

          {/* Display only the selected grid */}
          {activeTab === 0 && renderGridSection("Pending Fee Head Approvals", draftRows, false)}

          {activeTab === 1 && renderGridSection("Approved Fee Heads", approvedRows, true)}
        </Paper>
      </Stack>

      <Menu
        anchorEl={exportMenu.anchorEl}
        open={Boolean(exportMenu.anchorEl)}
        onClose={() => setExportMenu({ anchorEl: null, rows: [], label: "" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: 190,
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.18)",
          },
        }}
      >
        <MenuItem disableRipple>
          <Button
            fullWidth
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            sx={{
              minWidth: 165,
              backgroundColor: "#d32f2f",
              color: "#fff",
              textTransform: "none",
              "&:hover": { backgroundColor: "#b71c1c" },
            }}
            onClick={() => {
              exportPdf(exportMenu.rows, exportMenu.label);
              setExportMenu({ anchorEl: null, rows: [], label: "" });
            }}
          >
            DOWNLOAD PDF
          </Button>
        </MenuItem>
        <MenuItem disableRipple>
          <Button
            fullWidth
            variant="contained"
            startIcon={<TableViewIcon />}
            sx={{
              minWidth: 165,
              backgroundColor: "#4caf50",
              color: "#fff",
              textTransform: "none",
              "&:hover": { backgroundColor: "#357a38" },
            }}
            onClick={() => {
              exportExcel(exportMenu.rows, exportMenu.label);
              setExportMenu({ anchorEl: null, rows: [], label: "" });
            }}
          >
            DOWNLOAD EXCEL
          </Button>
        </MenuItem>
      </Menu>

      <Dialog open={Boolean(confirm)} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm action</DialogTitle>
        <DialogContent>
          <Typography>{confirmationText}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirm(null)}
            sx={{
              backgroundColor: "#d32f2f",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#b71c1c",
                color: "#fff",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            color={confirm?.action === "approve" ? "success" : "error"}
            variant="contained"
            onClick={runAction}
            sx={{
              backgroundColor: "#2e7d32",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#1b5e20",
              },
            }}
          >
            {confirm?.action === "approve"
              ? "Yes, Approve"
              : confirm?.action === "decline"
              ? "Yes, Decline"
              : confirm?.action === "reject"
              ? "Yes, Reject"
              : "Yes, Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4500}
        onClose={() => setSnackbar((old) => ({ ...old, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((old) => ({ ...old, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
